import * as Yup from 'yup';
import { Navigate, useRoutes, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useState, useEffect, useRef } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// config
import { PATH_AFTER_LOGIN } from 'src/config-global';
import { paths } from 'src/routes/paths';

// auth
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import httpCommon from 'src/http-common';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { decryptEmail } from 'src/utils/crypto-helper';
import { Box, IconButton, InputAdornment } from '@mui/material';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Popper from '@mui/material/Popper';
import Iconify from 'src/components/iconify';
import { useBoolean } from 'src/hooks/use-boolean';
import Swal from 'sweetalert2';
import { storage } from 'src/utils/storage';
import { useSnackbar } from 'src/components/snackbar';
import { encryptData } from './encryptionUtils';
import { fetchDefaultSiteCode, getActiveClient } from 'src/_core/config/clientSelector';

// ----------------------------------------------------------------------

export default function JwtLoginView() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [drpSata, setDrpData] = useState([]);
  const [drpFilter,setDrpFilter] =useState([])
  const [selectedValue, setSelectedValue] = useState(null);
  const [siteError, setSiteError] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(drpSata);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotLoginId, setForgotLoginId] = useState('');
  const [lookupEmail, setLookupEmail] = useState('');
  const [forgotStep, setForgotStep] = useState('enterId'); // 'enterId' | 'confirmEmail' | 'done'
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSite, setForgotSite] = useState(null);
  const [forgotLoginIdError, setForgotLoginIdError] = useState('');
  const [forgotSiteError, setForgotSiteError] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const debounceTimerRef = useRef(null);
  const siteInputRef = useRef(null);
  const forgotLoginIdRef = useRef(null);
  const forgotSiteInputRef = useRef(null);
  
  const maskEmail = (email) => {
    if (!email || typeof email !== 'string') return email;
    const parts = email.split('@');
    if (parts.length !== 2) return email;
    const [local, domain] = parts;
    const maskLocal = () => {
      if (local.length <= 2) return `${local[0] || ''}*`;
      return `${local[0]}${'*'.repeat(local.length - 2)}${local[local.length - 1]}`;
    };
    const maskDomain = () => {
      const domainParts = domain.split('.');
      if (domainParts.length === 0) return domain;
      const first = domainParts[0];
      if (!first) return domain;
      const maskedFirst = first.length <= 2 ? `${first[0] || ''}*` : `${first.slice(0, 2)}${'*'.repeat(first.length - 2)}`;
      return [maskedFirst, ...domainParts.slice(1)].join('.');
    };
    return `${maskLocal()}@${maskDomain()}`;
  };
 
  const [errorMsg, setErrorMsg] = useState('');
  const password = useBoolean();

  const LoginSchema = Yup.object().shape({
    loginId: Yup.string().required('Staff ID is required'),
    password: Yup.string().required('password is required'),

    hospital: Yup.string().required('Hospital is required'),
  });

  useEffect(() => {
    fetchFilterDropdown();
  }, []);


  const fetchFilterDropdown = async () => {
    try {
      const response = await httpCommon.get('/get_sitecode.php');


      if (response.data.status === 'SUCCESS') {
      
        const data=response.data.data;
     
        const uniqueSiteCd = new Set();

    // Filter the data to ensure unique site_cd
    const filteredData = data.filter(item => {
        if (!uniqueSiteCd.has(item.site_cd)) {
            uniqueSiteCd.add(item.site_cd);
            return true; // Include this item in the filtered data
        }
        return false; // Exclude this item from the filtered data
    });

      setDrpFilter(filteredData)

        setDrpData(response.data.data);
   
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const defaultValues = {
    loginId: '',
    password: '',
    hospital: '',
  };
  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    formState: { isSubmitting },
    clearErrors,
    setError,
    setFocus,
  } = methods;

  const swalFront = (options) => {
    return Swal.fire({
      ...options,
      didOpen: () => {
        const container = Swal.getContainer();
        if (container) container.style.zIndex = '2001';
      },
    });
  };

  const handleSubmit = async (e) => {
  
    e.preventDefault();
    const siteOption =
      selectedValue ||
      drpFilter.find((item) => item.site_cd === methods.getValues().hospital) ||
      null;
    const site = siteOption?.site_cd || '';
    const loginId = methods.getValues().loginId?.trim() || '';
    methods.setValue('loginId', loginId);
    const passwordParam = methods.getValues().password;

    clearErrors(['loginId', 'password']);
    setSiteError('');

    const validationErrors = {
      loginId: !loginId,
      password: !passwordParam,
      site: !site,
    };

    if (validationErrors.loginId) {
      setError('loginId', { type: 'required', message: 'Login ID is required' });
    }
    if (validationErrors.password) {
      setError('password', { type: 'required', message: 'Password is required' });
    }
    if (validationErrors.site) {
      setSiteError('Site is required');
    }

    if (validationErrors.loginId || validationErrors.password || validationErrors.site) {
      if (validationErrors.loginId) {
        setFocus('loginId');
      } else if (validationErrors.password) {
        setFocus('password');
      } else {
        siteInputRef.current?.focus();
      }
      return;
    }

      try {
        const response = await httpCommon.get(
          `/authenticate_login.php?login_id=${encodeURIComponent(loginId)}&password=${encodeURIComponent(passwordParam)}&site_cd=${encodeURIComponent(site)}`
        );

        if (response.data.status === 'SUCCESS') {
          const LoginData =  response.data.data[0];          
          // Store login data first
          storage.set('loginId', encryptData(loginId));
          storage.set('siteCode', encryptData(siteOption.site_cd));
          storage.set('siteName', encryptData(siteOption.site_name));
          storage.set("employeeId", encryptData(LoginData.emp_mst_empl_id))
          storage.set("employeeName", encryptData(LoginData.emp_mst_name))
          storage.set("department", encryptData(LoginData.emp_det_department))
          storage.set('ldapAuthWw', String(response.data.dft_mst_ldap_auth_ww ?? '0'))

          // Fetch default site code to determine client config BEFORE navigation
          // This must complete before any components try to use client config
          try {
            await fetchDefaultSiteCode();
          } catch (error) {
            console.error('Error fetching default site code during login:', error);
            // Continue with login even if this fails - will use default client
          }

          // Determine redirect path based on client config
          const activeClient = getActiveClient();
          const redirectPath =
            activeClient === 'SMCD_CUSTOMIZATION' || activeClient === 'SMCI_CUSTOMIZATION'
            ? paths.dashboard.work_request.byDepartment 
            : PATH_AFTER_LOGIN;

          swalFront({
            title: 'Login Success',
            text: 'Welcome to Evantage Webwork',
            icon: 'success',
            showConfirmButton: false,
            allowOutsideClick: false,
            timer: 2000,
          });

          setTimeout(() => {
            navigate(redirectPath, { replace: true });
          }, 2000);
        } else {
          enqueueSnackbar(
            response.data.message || 'Invalid username or password. Please try again.',
            {
              variant: 'error',
              anchorOrigin: { vertical: 'top', horizontal: 'center' },
            }
          );
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
  };

  // Simple modal popper
  const ModalAutocompletePopper = (props) => (
    <Popper
      {...props}
      placement="bottom-start"
    />
  );
  
  // Forgot password flow
  const handleOpenForgot = async () => {
    setForgotLoginId('');
    setLookupEmail('');
    setForgotStep('enterId');
    setForgotLoginIdError('');
    setForgotSiteError('');
    // Do not preselect from login form; start with no selection
    setForgotSite(null);
    if (!drpFilter || drpFilter.length === 0) {
      await fetchFilterDropdown();
    }
    setForgotOpen(true);
  };

  const handleCloseForgot = () => {
    setForgotOpen(false);
    setIsDropdownOpen(false);
    setForgotLoginIdError('');
    setForgotSiteError('');
  };

  const handleLookupEmail = async () => {
    const siteCd = forgotSite?.site_cd || '';
    const loginId = forgotLoginId?.trim() || '';
    setForgotLoginId(loginId);

    setForgotLoginIdError('');
    setForgotSiteError('');

    const validationErrors = {
      loginId: !loginId,
      site: !siteCd,
    };

    if (validationErrors.loginId) {
      setForgotLoginIdError('Login ID is required');
    }
    if (validationErrors.site) {
      setForgotSiteError('Site is required');
    }

    if (validationErrors.loginId || validationErrors.site) {
      if (validationErrors.loginId) {
        forgotLoginIdRef.current?.focus();
      } else {
        forgotSiteInputRef.current?.focus();
      }
      return;
    }

    const snackbarAnchor = { vertical: 'top', horizontal: 'center' };

    try {
      setForgotLoading(true);

      let ldapResponse;
      try {
        ldapResponse = await httpCommon.post('/get_LDAP_checking.php', {
          site_cd: siteCd,
          login_id: loginId,
        });
      } catch (ldapError) {
        enqueueSnackbar(
          ldapError?.response?.data?.message || 'Unable to verify account.',
          {
            variant: 'error',
            anchorOrigin: snackbarAnchor,
          }
        );
        return;
      }

      if (ldapResponse?.data?.status !== 'SUCCESS') {
        enqueueSnackbar(
          ldapResponse?.data?.message || 'Unable to verify account.',
          {
            variant: 'error',
            anchorOrigin: snackbarAnchor,
          }
        );
        return;
      }

      if (Number(ldapResponse.data.data?.ldap_auth) === 1) {
        enqueueSnackbar(
          'This account is linked to LDAP. Password reset is not allowed. Please contact the administrator.',
          {
            variant: 'warning',
            anchorOrigin: snackbarAnchor,
          }
        );
        return;
      }

      const requestData = {
        login_id: loginId,
        mst_rowID: 0,
        isCreatingWorkReq: true,
        site_cd: siteCd,
      };
      const response = await httpCommon.post('/GetUserInfo.php', requestData);
      if (response?.data?.status === 'SUCCESS') {
        const res = response.data.data?.query1_response?.[0];
        const encryptedEmail = res?.emp_det_email_id || '';
        if (!encryptedEmail) {
          enqueueSnackbar(
            'There was no email for this account. Please contact administrator.',
            {
              variant: 'error',
              anchorOrigin: snackbarAnchor,
            }
          );
          return;
        }
        const email = decryptEmail(encryptedEmail);
        setLookupEmail(email || '');
        setForgotStep('confirmEmail');
      } else {
        swalFront({ title: 'User not found', icon: 'error', timer: 1800, showConfirmButton: false });
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar(error?.response?.data?.message || 'Lookup failed', {
        variant: 'error',
        anchorOrigin: snackbarAnchor,
      });
    } finally {
      setForgotLoading(false);
    }
  };

  const handleTriggerReset = async () => {
    try {
      setForgotLoading(true);
      // Call backend to trigger reset for this login_id / email
      // Adjust endpoint to your API when available
      const siteCd = (forgotSite && forgotSite.site_cd) || selectedValue?.site_cd || localStorage.getItem('siteCode') || '';
      const loginId = forgotLoginId?.trim() || '';
      setForgotLoginId(loginId);
      const payload = { login_id: loginId };
      if (siteCd) payload.site_cd = siteCd;
      const response = await httpCommon.post('/trigger_password_reset.php', payload);
      if (response?.data?.status === 'SUCCESS') {
        setForgotStep('done');
      } else {
        swalFront({ title: 'Unable to trigger reset', icon: 'error', timer: 1800, showConfirmButton: false });
      }
    } catch (error) {
      console.error(error);
      swalFront({ title: 'Request failed', icon: 'error', timer: 1800, showConfirmButton: false });
    } finally {
      setForgotLoading(false);
    }
  };

  
  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // handle Id change — preserve user-entered casing
  const handleIdChange = async (event) => {
    const newId = event.target.value;
    methods.setValue('loginId', newId);
    clearErrors('loginId');

    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (newId.trim() !== '') {
      // Call API after user stops typing (500ms debounce)
      debounceTimerRef.current = setTimeout(async () => {
        try {
          const response = await httpCommon.get(
            `/get_auto_select_default_site_code.php?emp_mst_login_id=${encodeURIComponent(newId)}`
          );

          if (response.data.status === 'SUCCESS' && response.data.data && response.data.data.length > 0) {
            const defaultSite = response.data.data[0].default_site;
            if (defaultSite) {
              // Find and select the matching site
              const matchingSite = drpFilter.find((item) => item.site_cd && item.site_cd.toLowerCase() === defaultSite.toLowerCase());
              if (matchingSite) {
                setSelectedValue(matchingSite);
                methods.setValue('hospital', matchingSite.site_cd);
                clearErrors('hospital');
                setSiteError('');
              }
            }
          }
        } catch (error) {
          console.error('Error fetching default site:', error);
        }
      }, 500);
    } else {
      // If login ID is empty, clear selection
      setSelectedValue(null);
      methods.setValue('hospital', '');
    }
  };

  const renderForm = (
    <Stack spacing={3}>
      {!!errorMsg && <Alert severity="error">{errorMsg}</Alert>}
  
      {/* Login ID */}
      <RHFTextField
        name="loginId"
        label="Login ID"
        autoFocus
        onChange={handleIdChange}
        InputProps={{
          sx: {
            backgroundColor: 'white',
            borderRadius: '8px',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e0e0e0',
              borderWidth: '1px',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#1e40af',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#1e40af',
              borderWidth: '2px',
            },
            '& input': {
              color: '#333',
            },
          },
        }}
        InputLabelProps={{
          sx: {
            color: '#666',
            '&.Mui-focused': {
              color: '#1e40af',
            },
            '&.MuiInputLabel-shrink': {
              color: '#1e40af',
            },
          },
        }}
      />

      {/* Password */}
      <RHFTextField
        name="password"
        label="Password"
        type={password.value ? 'text' : 'password'}   
        onChange={(event) => {
          methods.setValue('password', event.target.value);
          clearErrors('password');
        }}
        InputProps={{
          sx: {
            backgroundColor: 'white',
            borderRadius: '8px',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e0e0e0',
              borderWidth: '1px',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#1e40af',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#1e40af',
              borderWidth: '2px',
            },
            '& input': {
              color: '#333',
            },
          },
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={password.onToggle} edge="end">
                <Iconify
                  icon={password.value ? 'solar:eye-bold' : 'ri:eye-off-fill'}
                  color="#666"
                />
              </IconButton>
            </InputAdornment>
          ),
        }}
        InputLabelProps={{
          sx: {
            color: '#666',
            '&.Mui-focused': {
              color: '#1e40af',
            },
            '&.MuiInputLabel-shrink': {
              color: '#1e40af',
            },
          },
        }}
      />

      {/* Site Dropdown */}
      <Autocomplete
        name="hospital"
        value={selectedValue}
        onChange={(event, newValue) => {
          setSelectedValue(newValue);
          methods.setValue('hospital', newValue?.site_cd || '');
          clearErrors('hospital');
          setSiteError('');
          setErrorMsg('');
        }}
        options={drpFilter}
        getOptionLabel={(option) => `${option.site_name} `}
        getOptionValue={(option) => option.site_cd}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Site"
            variant="outlined"
            error={!!siteError}
            helperText={siteError}
            inputRef={siteInputRef}
            InputProps={{
              ...params.InputProps,
              sx: {
                backgroundColor: 'white',
                borderRadius: '8px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#e0e0e0',
                  borderWidth: '1px',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1e40af',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1e40af',
                  borderWidth: '2px',
                },
                '& input': {
                  color: '#333',
                },
              },
            }}
            InputLabelProps={{
              sx: {
                color: '#666',
                '&.Mui-focused': {
                  color: '#1e40af',
                },
                '&.MuiInputLabel-shrink': {
                  color: '#1e40af',
                },
              },
            }}
          />
        )}
      />
      
      {/* Forgot password link under Site field */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -1 }}>
        <Link
          component="button"
          type="button"
          onClick={handleOpenForgot}
          variant="body2"
          color="inherit"
          underline="always"
          sx={{ fontSize: 12, background: 'none', border: 0, p: 0, cursor: 'pointer' }}
        >
          Forgot password?
        </Link>
      </Box>
  
      {!!errorMsg && <Alert severity="error">{errorMsg}</Alert>}
  
      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
        sx={{
          background: '#1e40af',
          color: 'white',
          fontWeight: 600,
          padding: '14px 20px',
          borderRadius: '8px',
          fontSize: '16px',
          textTransform: 'none',
          boxShadow: '0 4px 12px rgba(30, 64, 175, 0.3)',
          '&:hover': {
            background: '#1e3a8a',
            boxShadow: '0 6px 16px rgba(30, 64, 175, 0.4)',
          },
        }}
      >
        Login Now
      </LoadingButton>

        <Box sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
          <Typography
            variant="body2"
            sx={{
              color: 'gray',
              fontSize: '11px',
            }}
          >
            © 2025 Evantage Solutions Sdn Bhd. All rights reserved.
          </Typography>
        </Box>
    </Stack>
  );
  
  return (
    <>
      {/* Forgot Password Modal */}
      <Dialog 
        open={forgotOpen} 
        onClose={handleCloseForgot} 
        fullWidth 
        maxWidth="xs" 
        sx={{ 
          zIndex: 2000,
          '& .MuiDialog-paper': {
            minHeight: isDropdownOpen ? '400px' : 'auto',
            transition: 'min-height 0.2s ease'
          }
        }}
      >
        <DialogTitle>
          {forgotStep === 'enterId' && 'Do you want to change password?'}
          {forgotStep === 'confirmEmail' && 'Is this your email?'}
          {forgotStep === 'done' && 'Request sent'}
        </DialogTitle>
        <DialogContent>
          {forgotStep === 'enterId' && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Please insert your Login ID and select Site.
              </Typography>
              <TextField
                label="Login ID"
                value={forgotLoginId}
                onChange={(e) => {
                  const newId = e.target.value || '';
                  setForgotLoginId(newId);
                  setForgotLoginIdError('');
                  const userData = drpSata.find(
                    (item) =>
                      item.emp_mst_login_id &&
                      item.emp_mst_login_id.toUpperCase() === newId.toUpperCase()
                  );
                  if (userData) {
                    const option = drpFilter.find((opt) => opt.site_cd === userData.site_cd);
                    setForgotSite(option || null);
                    if (option) {
                      setForgotSiteError('');
                    }
                  } else if (!forgotSite) {
                    setForgotSite(null);
                  }
                }}
                inputRef={forgotLoginIdRef}
                error={!!forgotLoginIdError}
                helperText={forgotLoginIdError}
                autoFocus
                fullWidth
              />
              <Autocomplete
                value={forgotSite}
                onChange={(event, newValue) => {
                  setForgotSite(newValue);
                  setForgotSiteError('');
                }}
                options={drpFilter}
                isOptionEqualToValue={(option, value) => option?.site_cd === value?.site_cd}
                getOptionLabel={(option) => `${option.site_name} `}
                openOnFocus
                disablePortal
                onOpen={() => setIsDropdownOpen(true)}
                onClose={() => setIsDropdownOpen(false)}
                PopperComponent={ModalAutocompletePopper}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Site"
                    variant="outlined"
                    error={!!forgotSiteError}
                    helperText={forgotSiteError}
                    inputRef={forgotSiteInputRef}
                  />
                )}
              />
            </Stack>
          )}

          {forgotStep === 'confirmEmail' && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                We found this email linked to your account:
              </Typography>
              <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'grey.100' }}>
                <Typography variant="subtitle2">{maskEmail(lookupEmail)}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                If this is correct and you want to proceed, click Continue to trigger the reset.
              </Typography>
            </Stack>
          )}

          {forgotStep === 'done' && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2">
              Password reset successful! Check your email.
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {forgotStep === 'enterId' && (
            <>
              <Button onClick={handleCloseForgot} disabled={forgotLoading}>Cancel</Button>
              <LoadingButton
                onClick={handleLookupEmail}
                loading={forgotLoading}
                variant="contained"
                color="success"
              >
                Continue
              </LoadingButton>
            </>
          )}

          {forgotStep === 'confirmEmail' && (
            <>
              <Button onClick={() => setForgotStep('enterId')} disabled={forgotLoading}>Back</Button>
              <LoadingButton onClick={handleTriggerReset} loading={forgotLoading} variant="contained">
                Continue
              </LoadingButton>
            </>
          )}

          {forgotStep === 'done' && (
            <Button onClick={handleCloseForgot} variant="contained">Close</Button>
          )}
        </DialogActions>
      </Dialog>

      <FormProvider methods={methods} onSubmit={handleSubmit}>
        {renderForm}
      </FormProvider>
    </>
  );
}
